import { readonly, ref, type Ref } from 'vue';
import type { SessionRefreshResponse } from '@neon-one/auctions-types';
import { httpClient } from '@/shell/http';
import { adoptJwt } from '@/shell/auth/loadAdminUser';

interface SessionKeepAliveOptions {
    // How often to check if we should refresh (default: 4 minutes)
    checkIntervalMs?: number;
    // How recently there must have been activity to trigger a refresh (default: 10 minutes)
    activityWindowMs?: number;
    // How often to update the last-activity timestamp per user action (default: 30 seconds)
    activityThrottleMs?: number;
}

interface UseSessionKeepAliveReturn {
    start: () => void;
    stop: () => void;
    // Readonly: consumers may observe the last-activity timestamp but must not
    // overwrite the keep-alive's internal state.
    lastActivity: Readonly<Ref<number | null>>;
}

// Activity events that count as user engagement.
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'visibilitychange'] as const;

const DEFAULT_CHECK_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes
const DEFAULT_ACTIVITY_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const DEFAULT_ACTIVITY_THROTTLE_MS = 30 * 1000; // 30 seconds

export function useSessionKeepAlive(options: SessionKeepAliveOptions = {}): UseSessionKeepAliveReturn {
    const checkIntervalMs = options.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS;
    const activityWindowMs = options.activityWindowMs ?? DEFAULT_ACTIVITY_WINDOW_MS;
    const activityThrottleMs = options.activityThrottleMs ?? DEFAULT_ACTIVITY_THROTTLE_MS;

    const lastActivity = ref<number | null>(null);
    let intervalHandle: ReturnType<typeof setInterval> | null = null;
    let activityThrottleHandle: ReturnType<typeof setTimeout> | null = null;
    let isStarted = false;
    // Bumped on every start()/stop() so an in-flight refresh whose request
    // started under an earlier lifecycle can detect it is stale and skip
    // adopting a JWT (e.g. the user logged out while the request was pending).
    let lifecycleGeneration = 0;

    function updateActivity(): void {
        // Throttle: only update timestamp if enough time has passed since last update.
        if (activityThrottleHandle !== null) return;
        lastActivity.value = Date.now();
        activityThrottleHandle = setTimeout(() => {
            activityThrottleHandle = null;
        }, activityThrottleMs);
    }

    function handleVisibilityChange(): void {
        // Only count as activity when the document becomes visible (user returning to tab).
        if (document.visibilityState === 'visible') {
            updateActivity();
        }
    }

    function handleActivity(event: Event): void {
        if (event.type === 'visibilitychange') {
            handleVisibilityChange();
        } else {
            updateActivity();
        }
    }

    function shouldRefresh(lastActivityTimestamp: number | null, now: number): boolean {
        if (lastActivityTimestamp === null) return false;
        return now - lastActivityTimestamp < activityWindowMs;
    }

    async function refreshSession(): Promise<void> {
        const refreshGeneration = lifecycleGeneration;
        const now = Date.now();
        if (!shouldRefresh(lastActivity.value, now)) return;

        try {
            const resp = await httpClient().post<SessionRefreshResponse>('/admin/session/refresh');
            const newJwt = resp.data.jwt;
            // Drop the response if the hook was stopped (or restarted) while the
            // request was in flight — otherwise we could re-authenticate a
            // session the user just logged out of.
            if (isStarted && refreshGeneration === lifecycleGeneration && newJwt) {
                adoptJwt(newJwt);
            }
        } catch (err) {
            // Intentionally swallow refresh failures — the existing 401 interceptor
            // in httpClient will handle genuine session expiry. A refresh failure
            // could be transient (network blip, backend restart) and shouldn't
            // kill an otherwise-valid session.
        }
    }

    function checkAndRefresh(): void {
        refreshSession().catch(() => undefined);
    }

    function start(): void {
        if (isStarted) return;
        isStarted = true;
        lifecycleGeneration += 1;

        // Record initial activity timestamp on start.
        lastActivity.value = Date.now();

        // Register activity listeners.
        for (const eventType of ACTIVITY_EVENTS) {
            window.addEventListener(eventType, handleActivity, { passive: true });
        }

        // Start periodic refresh check.
        intervalHandle = setInterval(checkAndRefresh, checkIntervalMs);
    }

    function stop(): void {
        if (!isStarted) return;
        isStarted = false;
        lifecycleGeneration += 1;

        // Clear interval.
        if (intervalHandle !== null) {
            clearInterval(intervalHandle);
            intervalHandle = null;
        }

        // Clear throttle timer.
        if (activityThrottleHandle !== null) {
            clearTimeout(activityThrottleHandle);
            activityThrottleHandle = null;
        }

        // Remove activity listeners.
        for (const eventType of ACTIVITY_EVENTS) {
            window.removeEventListener(eventType, handleActivity);
        }

        lastActivity.value = null;
    }

    return { start, stop, lastActivity: readonly(lastActivity) };
}
